import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAudits, getDashboardStats, downloadRecommendationPdf, getPaymentStatus, subscribeToPlan, cancelSubscription } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CardSetup from '../components/CardSetup';

const HHCAPS_LABELS = {
  careOfPatients: 'Care of Patients',
  communicationWithPatients: 'Communication',
  specificCareIssues: 'Specific Care Issues',
  overallRating: 'Overall Rating',
  willingnessToRecommend: 'Willingness to Recommend',
};

export default function Dashboard() {
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashStats, setDashStats] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showCardSetup, setShowCardSetup] = useState(false);

  useEffect(() => {
    Promise.all([
      getAudits().then(res => setAudits(res.data.audits || [])),
      getDashboardStats().then(res => setDashStats(res.data)).catch(() => null),
      getPaymentStatus().then(res => {
        setPaymentStatus(res.data);
        if (!res.data.hasCard) setShowCardSetup(true);
      }).catch(() => {
        // Stripe not configured or endpoint error — still show card setup prompt
        setShowCardSetup(true);
        setPaymentStatus({ hasCard: false, paidAuditIds: [], reportPrice: 50 });
      }),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const completed = audits.filter(a => a.status === 'completed');
  const avgScore = completed.length > 0
    ? Math.round(completed.reduce((s, a) => s + (a.compliance_score || 0), 0) / completed.length)
    : '--';
  const totalCritical = completed.reduce((s, a) => s + (a.critical_count || 0), 0);
  const totalWarnings = completed.reduce((s, a) => s + (a.warning_count || 0), 0);

  // LUPA calculations
  const lupaMet = dashStats?.lupaSummary?.find(r => r.lupa_threshold_met === 'true');
  const lupaNotMet = dashStats?.lupaSummary?.find(r => r.lupa_threshold_met === 'false');
  const lupaTotal = (parseInt(lupaMet?.count || 0)) + (parseInt(lupaNotMet?.count || 0));
  const lupaMetPct = lupaTotal > 0 ? Math.round((parseInt(lupaMet?.count || 0) / lupaTotal) * 100) : null;

  // HHCAPS grouping
  const hhcapsByDomain = {};
  if (dashStats?.hhcapsSummary) {
    for (const row of dashStats.hhcapsSummary) {
      if (!hhcapsByDomain[row.domain]) hhcapsByDomain[row.domain] = { meets: 0, partial: 0, not_met: 0 };
      hhcapsByDomain[row.domain][row.score] = parseInt(row.count);
    }
  }

  // Weakness categories
  const weaknessMap = {};
  if (dashStats?.weaknessSummary) {
    for (const w of dashStats.weaknessSummary) {
      if (!weaknessMap[w.category]) weaknessMap[w.category] = { critical: 0, warning: 0, total: 0 };
      const count = parseInt(w.count);
      weaknessMap[w.category][w.severity] = count;
      weaknessMap[w.category].total += count;
    }
  }
  const sortedWeaknesses = Object.entries(weaknessMap).sort(([, a], [, b]) => b.total - a.total).slice(0, 6);

  const handleDownloadRec = async (reportId) => {
    try {
      const res = await downloadRecommendationPdf(reportId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ChartAudit-Recommendations-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download recommendation report');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Loading...</p></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Welcome back, {user?.firstName}</h1>
        <p className="text-gray-500 mt-1">{user?.agencyName} — Audit Dashboard</p>
      </div>

      {/* Card Setup Banner */}
      {showCardSetup && (
        <div className="mb-8">
          <CardSetup
            onComplete={() => { setShowCardSetup(false); setPaymentStatus(prev => ({ ...prev, hasCard: true })); }}
            onSkip={() => setShowCardSetup(false)}
          />
        </div>
      )}

      {/* Subscription & Payment Status Banner */}
      {paymentStatus?.hasCard && !showCardSetup && (
        <div className="mb-6 glass-card rounded-2xl border border-teal-100/50 p-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                {paymentStatus.subscription?.isActive ? (
                  <>
                    <p className="text-sm font-bold text-gray-800">Monthly Plan — $100/mo</p>
                    <p className="text-xs text-gray-500">
                      {paymentStatus.subscription.reportsRemaining} of {paymentStatus.subscription.reportsIncluded} reports remaining
                      {paymentStatus.subscription.reportsRemaining === 0 && <span className="text-amber-600 ml-1">($40/report overage)</span>}
                      {paymentStatus.subscription.billingPeriodEnd && (
                        <span> — Renews {new Date(paymentStatus.subscription.billingPeriodEnd).toLocaleDateString()}</span>
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-bold text-gray-800">Pay Per Report — $50/report</p>
                    <p className="text-xs text-gray-500">
                      {paymentStatus.cardInfo ? `${paymentStatus.cardInfo.brand} ending in ${paymentStatus.cardInfo.last4}` : 'Card on file'}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {paymentStatus.subscription?.isActive ? (
                <button
                  onClick={async () => {
                    if (!window.confirm('Cancel your monthly subscription? You\'ll switch to $50/report at the end of this billing period.')) return;
                    try {
                      await cancelSubscription();
                      const newStatus = await getPaymentStatus();
                      setPaymentStatus(newStatus.data);
                      alert('Subscription will cancel at end of billing period.');
                    } catch (err) {
                      alert(err.response?.data?.error || 'Failed to cancel');
                    }
                  }}
                  className="text-xs text-gray-500 hover:text-red-600 px-3 py-1.5 border border-gray-200 rounded-lg transition"
                >
                  Cancel Plan
                </button>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      const res = await subscribeToPlan();
                      if (res.data.success) {
                        const newStatus = await getPaymentStatus();
                        setPaymentStatus(newStatus.data);
                        alert('Subscribed to Monthly Plan! You get 4 reports included each month.');
                      } else if (res.data.requiresAction) {
                        alert('Additional verification needed. Please check your email or banking app.');
                      }
                    } catch (err) {
                      alert(err.response?.data?.error || 'Failed to subscribe');
                    }
                  }}
                  className="text-xs font-semibold text-white bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 px-4 py-1.5 rounded-lg shadow-sm transition"
                >
                  Upgrade to Monthly ($100/mo)
                </button>
              )}
            </div>
          </div>
          {paymentStatus.subscription?.status === 'canceling' && (
            <p className="text-xs text-amber-600 mt-2">Your subscription will end at the current billing period. You'll switch to pay-per-report after that.</p>
          )}
        </div>
      )}

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
          <p className="text-sm text-gray-500">Total Audits</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{audits.length}</p>
        </div>
        <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
          <p className="text-sm text-gray-500">Average Score</p>
          <p className="text-3xl font-bold text-teal-600 mt-1">{avgScore}</p>
        </div>
        <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
          <p className="text-sm text-gray-500">Critical Issues Found</p>
          <p className="text-3xl font-bold text-red-600 mt-1">{totalCritical}</p>
        </div>
        <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
          <p className="text-sm text-gray-500">Warnings Found</p>
          <p className="text-3xl font-bold text-yellow-600 mt-1">{totalWarnings}</p>
        </div>
      </div>

      {/* PDGM LUPA + HHCAPS Row */}
      {dashStats && (lupaTotal > 0 || Object.keys(hhcapsByDomain).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* PDGM LUPA Card */}
          {lupaTotal > 0 && (
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">PDGM LUPA Threshold</h3>
              <div className="flex items-center justify-between mb-3">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{lupaMet?.count || 0}</p>
                  <p className="text-xs text-gray-500">Met</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">{lupaNotMet?.count || 0}</p>
                  <p className="text-xs text-gray-500">Not Met</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-teal-600">{lupaMetPct}%</p>
                  <p className="text-xs text-gray-500">Met Rate</p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${lupaMetPct >= 80 ? 'bg-green-500' : lupaMetPct >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${lupaMetPct || 0}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Across {lupaTotal} audits with visit data
              </p>
            </div>
          )}

          {/* HHCAPS Card */}
          {Object.keys(hhcapsByDomain).length > 0 && (
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">HHCAPS Quality Measures</h3>
              <div className="space-y-3">
                {Object.entries(hhcapsByDomain).map(([domain, scores]) => {
                  const total = scores.meets + scores.partial + scores.not_met;
                  const meetsPct = total > 0 ? Math.round((scores.meets / total) * 100) : 0;
                  return (
                    <div key={domain}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          {HHCAPS_LABELS[domain] || domain}
                        </span>
                        <span className={`text-xs font-bold ${meetsPct >= 80 ? 'text-green-600' : meetsPct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {meetsPct}% meets
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full bg-green-500" style={{ width: `${meetsPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top Weakness Areas + Recommendations Row */}
      {dashStats && (sortedWeaknesses.length > 0 || dashStats.recommendationReports?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Top Weakness Areas */}
          {sortedWeaknesses.length > 0 && (
            <div className="glass-card rounded-2xl shadow-sm border border-red-100/50 p-6">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Top Charting Weakness Areas</h3>
              <div className="space-y-2">
                {sortedWeaknesses.map(([category, data], idx) => (
                  <div key={category} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-bold text-gray-400 w-5">{idx + 1}</span>
                      <span className="text-sm font-medium text-gray-800">{category}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {data.critical > 0 && (
                        <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {data.critical} critical
                        </span>
                      )}
                      {data.warning > 0 && (
                        <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {data.warning} warnings
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {dashStats.auditSettings && (
                <p className="text-xs text-gray-400 mt-3">
                  {dashStats.auditSettings.audits_since_last_recommendation} of {dashStats.auditSettings.recommendation_trigger_count} audits until next recommendation report
                </p>
              )}
            </div>
          )}

          {/* Recommendation Reports */}
          {dashStats.recommendationReports?.length > 0 && (
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Improvement Recommendation Reports</h3>
              <div className="space-y-3">
                {dashStats.recommendationReports.map(report => (
                  <div key={report.id} className="flex items-center justify-between bg-teal-50/50 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        Report — {new Date(report.generated_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Based on {report.audit_count} audits — {report.total_weaknesses} issues analyzed
                      </p>
                    </div>
                    <button
                      onClick={() => handleDownloadRec(report.id)}
                      className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition"
                    >
                      Download PDF
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-4 mb-8">
        <Link to="/upload"
          className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition">
          Upload & Audit New Chart
        </Link>
        <Link to="/audits"
          className="glass-card hover:bg-white text-gray-700 px-6 py-3 rounded-xl font-semibold border border-gray-200 shadow-sm hover:shadow transition">
          View All Audits
        </Link>
      </div>

      {/* Recent Audits */}
      <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Recent Audits</h2>
        </div>
        {audits.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl mb-3 opacity-50">
              <svg className="w-12 h-12 mx-auto text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-lg">No audits yet</p>
            <p className="mt-2">Upload a clinical chart to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {audits.slice(0, 10).map((audit) => (
              <Link key={audit.id} to={`/audits/${audit.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-teal-50/50 transition">
                <div>
                  <p className="font-medium text-gray-800">{audit.original_filename}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(audit.created_at).toLocaleDateString()} — {audit.initiated_by_name}
                    {audit.patient_reference && ` — Patient: ${audit.patient_reference}`}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  {audit.status === 'completed' ? (
                    <>
                      <div className="flex space-x-2 text-sm">
                        {audit.critical_count > 0 && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{audit.critical_count} critical</span>}
                        {audit.warning_count > 0 && <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">{audit.warning_count} warnings</span>}
                        {audit.info_count > 0 && <span className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{audit.info_count} info</span>}
                      </div>
                      <span className={`text-2xl font-bold ${audit.compliance_score >= 80 ? 'text-green-600' : audit.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {audit.compliance_score}
                      </span>
                    </>
                  ) : audit.status === 'processing' ? (
                    <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm animate-pulse">Processing...</span>
                  ) : (
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">Failed</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
